import { useState } from "react";
import { useTableManagerContext } from "@/providers/AppendTableProvider";
import { BuildAppendTableRFC } from "@/Utils/Promises";
import { QManageTablesRFC } from "@coreapp/CoreApp/QManageTablesRFC";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";
import { appstrp } from "@coreapp/utils/appstrp";
import { useToast } from "@/providers/ToastProvider";
import { GetWSMgr } from "@/Utils/StateLib";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import { useChartSettingsContext } from "@/providers/ChartSettingsProvider";

export const useAppendTableManagement = wsMgr => {
  const [selectedTableID, setSelectedTableID] = useState(null);
  const appendTables = wsMgr?.fOrgInfo ? wsMgr.fOrgInfo.GetAppendTableList() : [];
  const { chartConfigRef } = useChartSettingsContext();
  const currentChartConfig = chartConfigRef.current;
  const { addToast } = useToast();

  const {
    tableManager,
    tableEditorContextValue: { onLoadTable }
  } = useTableManagerContext();

  const createTable = async tableName => {
    const { theTable, overrides, code } = await BuildAppendTableRFC(
      { table: { name: tableName } },
      "newappendtable"
    );

    addToast({
      ...createBoldToastMessage("Entity created", { tableName }),
      status: "success",
      timeoutMS: 5000
    });
    checkSetDefaultTable(theTable);
    onLoadTable(theTable, overrides, code);
  };

  const checkSetDefaultTable = firstTable => {
    const wsMgr = GetWSMgr();
    const appendTables = wsMgr.fOrgInfo.GetAppendTableList() ?? [];
    if (appendTables.length === 1) {
      wsMgr.BroadcastTopic("Auto Set Default Table", firstTable);
    }
  };

  const openTable = async (tableID, chartID) => {
    const { theTable, overrides, code } = await BuildAppendTableRFC(
      { ID: tableID },
      "loadappendtable",
      chartID ?? currentChartConfig?.ID
    );
    onLoadTable(theTable, overrides, code);
  };

  const tableManagerRFC = (mode, tableData) => {
    return new Promise((resolve, reject) => {
      const wsMgr = GetWSMgr();
      if (!wsMgr) {
        reject();
        return;
      }
      const manageTablesRFC = new QManageTablesRFC();
      const callbackObj = {
        fTableMgr: tableManager.state,
        resolve,
        reject
      };
      manageTablesRFC.InitUpdateTables(callbackObj);
      manageTablesRFC.SetParams(
        wsMgr.GetAccountName(),
        wsMgr.GetUserName(),
        mode,
        JSON.stringify(tableData)
      );
      Globals._rfcMgr.QueueRFC(manageTablesRFC);
    });
  };

  const getTableNamesInUse = () => {
    return appendTables ? appendTables.map(item => item.name) : [];
  };

  const handleCreateTable = (name = "") => {
    const finalName = name.trim();
    const currentValidation = validateTableName(finalName);
    if (currentValidation.isValid) {
      createTable(finalName);
    }
    return currentValidation;
  };

  const getDefaultNewTableName = () => {
    const existingTableNames = getTableNamesInUse();
    return existingTableNames.GetUniqueDuplicateName(appstr("New Table (name)"), "");
  };

  const handleManageTableAction = (action, tableData) => {
    switch (action) {
      case "open":
        openTable(tableData.ID);
        break;
      case "delete":
        wsMgr.CreateReactAlert(
          appstrp("Delete", " ") + "'" + tableData.name + "' ?",
          appstr("tabledeleteconfirm"),
          () => {
            tableManagerRFC("deleteappendtable", tableData)
              .then(() => {
                addToast({
                  ...createBoldToastMessage("Entity deleted", { entity: tableData.name }),
                  status: "success",
                  timeoutMS: 5000
                });
              })
              .catch(() => {
                addToast({ message: appstr("Generic toast error"), status: "error" });
              });
          },
          null,
          appstr("Delete Table"),
          appstr("Cancel")
        );
        break;
      case "rename":
        const newName = tableData.name;
        const tableID = tableData.ID;
        const existingTable = appendTables.find(table => table.ID === tableID);
        const invalidTableNames = getTableNamesInUse();
        if (newName.length < 4 || invalidTableNames.includes(newName)) {
          addToast({
            message: appstr("Table name must be at least 4 characters and unique"),
            status: "error"
          });
          break;
        }
        tableManagerRFC("renameappendtable", tableData)
          .then(() => {
            addToast({
              ...createBoldToastMessage("Entity renamed", {
                oldEntity: existingTable.name,
                newEntity: tableData.name
              }),
              status: "success",
              timeoutMS: 5000
            });
          })
          .catch(() => {
            addToast({ message: appstr("Generic toast error"), status: "error" });
          });
        break;
    }
  };

  const validateTableName = name => {
    const trimmedName = name?.trim() || "";
    const existingNames = getTableNamesInUse();

    if (!trimmedName || trimmedName.length === 0 || trimmedName.match(/^\s+$/)) {
      return {
        isValid: false,
        status: "error",
        errorMsg: appstr("Table name cannot be empty")
      };
    }

    if (trimmedName.length < 4) {
      return {
        isValid: false,
        status: "error",
        errorMsg: appstr("Table name must be at least 4 characters")
      };
    }

    if (existingNames.includes(trimmedName)) {
      return {
        isValid: false,
        status: "error",
        errorMsg: appstr("Table name already exists")
      };
    }

    return {
      isValid: true,
      status: "default",
      errorMsg: ""
    };
  };

  return {
    selectedTableID,
    setSelectedTableID,
    appendTables,
    validateTableName,
    getDefaultNewTableName,
    createTable,
    openTable,
    getTableNamesInUse,
    handleCreateTable,
    handleManageTableAction
  };
};
